import { Trans, t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Divider,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import dayjs from 'dayjs'

import { useGetTimeTrackingAuditLogsQuery } from '@/core/api'
import { auditActionLabel, auditReasonLabel, auditRoleLabel, formatAuditValue } from '../lib/auditFormat'

type AuditLogDialogProps = {
  sessionId: string | null
  onClose: () => void
}

export function AuditLogDialog({ sessionId, onClose }: AuditLogDialogProps) {
  useLingui()

  const { data: logs = [], isLoading } = useGetTimeTrackingAuditLogsQuery(
    { sessionId: sessionId ?? undefined, limit: 100 },
    { skip: !sessionId }
  )

  return (
    <Dialog open={!!sessionId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <HistoryRoundedIcon color="primary" />
          <Typography variant="h6" component="span">
            <Trans>Session history</Trans>
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : logs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            <Trans>No audit log entries for this session.</Trans>
          </Typography>
        ) : (
          <Stack spacing={2} divider={<Divider flexItem />}>
            {logs.map((log) => (
              <Stack key={log._id} spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {auditActionLabel(log.action)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {dayjs(log.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={auditRoleLabel(log.performedByRole)} size="small" variant="outlined" />
                  {log.reason && <Chip label={auditReasonLabel(log.reason)} size="small" color="warning" variant="outlined" />}
                </Stack>
                {log.comment && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    "{log.comment}"
                  </Typography>
                )}
                {log.changes.length > 0 && (
                  <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                    {log.changes.map((change, idx) => (
                      <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ fontSize: '0.8125rem' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 72 }}>
                          {change.field}:
                        </Typography>
                        <Box component="span" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                          {formatAuditValue(change.field, change.oldValue)}
                        </Box>
                        <Box component="span">→</Box>
                        <Box component="span" sx={{ fontWeight: 500 }}>
                          {formatAuditValue(change.field, change.newValue)}
                        </Box>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <Trans>Close</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
